import type { ActivityFirestore } from "../../ActivitiesContext";
import { ActivityStatus, ActivityUIKind } from "../../types";
import type { ActivityViewData } from "../../types";
import { convertTask, type FirestoreTask } from "../AsyncTaskService";
import type { ActivityEventProvider, FirestoreActivityEvent } from "./types";

function toDate(ts: any): Date {
    return ts?.toDate?.() ?? new Date();
}

/**
 * Provider for activity events with kind "async_task".
 *
 * Enrichment: subscribes to the individual async_tasks/{entity.id} document
 * so the UI gets real-time progress, status, and error updates. The activity
 * event controls the notification's existence; the async task supplies the
 * display details.
 */
export const asyncTaskProvider: ActivityEventProvider = {
    uiKind: ActivityUIKind.AsyncTask,

    convert(event: FirestoreActivityEvent, enrichment?: unknown): ActivityViewData {
        const task = enrichment as FirestoreTask | undefined;
        if (task) {
            return { ...convertTask(task), id: event.id };
        }

        return {
            id: event.id,
            kind: ActivityUIKind.AsyncTask,
            title: (event.payload?.title as string) || "Background task",
            description: (event.payload?.description as string) || null,
            status: ActivityStatus.Pending,
            icon: "far fa-cog",
            targetUrl: event.entity.url || null,
            openInNewTab: false,
            progress: null,
            error: null,
            createdAt: toDate(event.createdAt),
            completedAt: null
        };
    },

    subscribeToEnrichment(
        event: FirestoreActivityEvent,
        onData: (data: unknown) => void,
        firestore: ActivityFirestore
    ): () => void {
        return firestore.onDocSnapshot(
            "async_tasks",
            event.entity.id,
            (doc) => {
                onData(doc.exists ? ({ ...doc.data(), id: doc.id } as FirestoreTask) : undefined);
            },
            (error: any) => {
                // eslint-disable-next-line no-console
                console.error(
                    `asyncTaskProvider: enrichment listener error for ${event.entity.id}:`,
                    error
                );
            }
        );
    }
};
